'use client'

import CreationToolCutOutMode from 'creation/src/creation-tools/types/cut-out-mode'
import { useSessionStorage } from 'react-use'
import { cn } from '@/util/cn'
import { cutOutModeColor } from '../../presentation/cut-out-mode'
import { title } from '../../presentation/typography'
import { useCutOut } from './cut-out-context'

// Legacy structural reference: components/cut-out/instructions.tsx:50-84.
//
// Spec §12.5 — legacy calls `useSessionStorage(`${mode}_INSTRUCTIONS_SEEN`, {…})`,
// writing a complete four-mode object under a different key per mode and
// reading only `[mode]` back out. Three quarters of every write is dead. This
// port uses one key holding one object instead — sessionStorage dies with the
// tab, so there is nothing to migrate.
const STORAGE_KEY = 'CUT_OUT_INSTRUCTIONS_SEEN'

type SeenByMode = Record<CreationToolCutOutMode, boolean>

const initialSeen: SeenByMode = {
    [CreationToolCutOutMode.Lasso]: false,
    [CreationToolCutOutMode.Paint]: false,
    [CreationToolCutOutMode.Erase]: false,
    [CreationToolCutOutMode.MagicWand]: false,
}

const Instructions = () => {
    const { mode, modeInstructionPreview } = useCutOut()
    const [hasSeen, setHasSeen] = useSessionStorage<SeenByMode>(STORAGE_KEY, initialSeen)

    const previewOrMode = modeInstructionPreview || mode
    const instructions = CreationToolCutOutMode.instructions(previewOrMode)
    const instructionImageUrl = CreationToolCutOutMode.instructionsImageUrl(previewOrMode)

    const onClick = () => {
        setHasSeen({
            ...hasSeen,
            [mode]: true,
        })
    }

    if (hasSeen[mode] && !modeInstructionPreview) {
        return null
    }

    return (
        <div
            data-testid="instructions"
            onClick={onClick}
            className={cn(
                'absolute top-0 left-0 flex min-h-full w-full animate-ct-fade-in cursor-pointer flex-col items-center justify-center text-white'
            )}
        >
            <div className={cn('absolute top-0 left-0 h-full w-full opacity-85', cutOutModeColor(previewOrMode))} />
            <div className="relative p-[20px] text-center">
                {instructionImageUrl && (
                    <img src={instructionImageUrl} alt="" className="mx-auto block h-[100px] w-auto" />
                )}
                <div className={title}>{instructions}</div>
            </div>
        </div>
    )
}

export default Instructions
